import * as React from "react";
import { useId, useMergeRefs, FloatingPortal, FloatingFocusManager, useFloating, autoUpdate, offset, flip, shift, size, arrow, useHover, safePolygon, useClick, useDismiss, useRole, useInteractions, useTransitionStatus } from "@floating-ui/react";
import clsx from "clsx";
import { CloseButton } from "../close-button/close-button.mjs";
import { HeaderContext } from "../helpers/header-provider/header-provider.mjs";
import "debounce";
import "tabbable";
import { useIsMounted } from "../helpers/helper/hooks/use-is-mounted.mjs";
import { InputContext } from "../helpers/input-provider/input-provider.mjs";
import { useLabels } from "../helpers/label-provider/use-labels.mjs";
import { usePropsChecker } from "../helpers/props-checker/props-checker.mjs";
import { useTheme } from "../helpers/theme-provider/use-theme.mjs";
const Tooltip = (props) => {
  var _a, _b, _c, _d;
  usePropsChecker({
    componentProps: props,
    componentName: "Tooltip",
    defaultValueName: "defaultIsShown",
    valueName: "isShown",
    onChangeNames: "onToggle",
    onChangeAltNames: ["onShow", "onHide"]
  });
  const labels = useLabels();
  const { input: propInputIntent } = React.useContext(InputContext);
  const { portalRoot: portalContainer } = useTheme();
  const {
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    children,
    className,
    fixedWidth = true,
    noPadding,
    size: size2,
    onHeader,
    isCloseButtonVisible = true,
    trigger,
    referenceElement,
    elementRef,
    modal = false
  } = props;
  const [referenceId, setReferenceId] = React.useState(null);
  const internalReferenceId = useId();
  const inputIntent = React.useMemo(() => {
    if (trigger === "click") {
      return "touch";
    }
    return propInputIntent;
  }, [trigger, propInputIntent]);
  const state = useTooltip({ ...props, inputIntent });
  const { setOpen } = state;
  const isMounted = useIsMounted();
  const ref = useMergeRefs([state.refs.setFloating, elementRef]);
  const handleCloseClick = React.useCallback(
    (event) => {
      event.preventDefault();
      setOpen(false);
    },
    [setOpen]
  );
  const isCloseButtonShown = React.useMemo(() => {
    return !!(isCloseButtonVisible && ["initial", "pointer", "touch", "keyboard"].indexOf(inputIntent) > -1);
  }, [isCloseButtonVisible, inputIntent]);
  const labelledBy = (_a = ariaLabelledBy != null ? ariaLabelledBy : referenceId) != null ? _a : internalReferenceId;
  React.useEffect(() => {
    if (React.isValidElement(referenceElement)) {
      setReferenceId(referenceElement.props.id);
    }
  }, [referenceElement]);
  const content = /* @__PURE__ */ React.createElement(
    "div",
    {
      ref,
      style: state.floatingStyles,
      className: clsx(
        "tooltip",
        "tooltip--trigger-".concat(inputIntent),
        "tooltip--placement-".concat(state.placement),
        "is-".concat(state.status),
        {
          ["tooltip--has-close-button"]: isCloseButtonShown,
          ["tooltip--fixed-width"]: !!fixedWidth,
          ["tooltip--no-padding"]: !!noPadding,
          ["tooltip--".concat(size2)]: !!size2,
          ["tooltip--on-header"]: !!onHeader
        },
        className
      ),
      tabIndex: -1,
      "aria-modal": modal ? "true" : void 0,
      "aria-labelledby": labelledBy,
      "aria-describedby": ariaDescribedBy,
      ...state.getFloatingProps({
        // Prevent click events from propagating to portal parents
        onClick: (e) => e.stopPropagation()
      })
    },
    /* @__PURE__ */ React.createElement("div", { className: "tooltip__inner" }, /* @__PURE__ */ React.createElement(
      "div",
      {
        className: "tooltip__arrow",
        ref: state.arrowRef,
        style: {
          position: "absolute",
          left: (_b = state.middlewareData.arrow) == null ? void 0 : _b.x,
          top: (_c = state.middlewareData.arrow) == null ? void 0 : _c.y
        }
      }
    ), /* @__PURE__ */ React.createElement("div", { className: "tooltip__content" }, isCloseButtonShown && /* @__PURE__ */ React.createElement(CloseButton, { className: "tooltip__close", onClick: handleCloseClick }), children))
  );
  const reference = React.isValidElement(referenceElement) ? React.cloneElement(
    referenceElement,
    state.getReferenceProps({
      ...referenceElement.props,
      ref: state.refs.setReference,
      id: (_d = referenceElement.props.id) != null ? _d : internalReferenceId
    })
  ) : null;
  if (!isMounted) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, reference, content);
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, reference, state.isMounted && /* @__PURE__ */ React.createElement(FloatingPortal, { root: portalContainer }, /* @__PURE__ */ React.createElement(
    FloatingFocusManager,
    {
      context: state.context,
      modal,
      initialFocus: state.reason === "hover" ? -1 : 0,
      returnFocus: true,
      visuallyHiddenDismiss: isCloseButtonShown ? false : labels.get("close")
    },
    content
  )));
};
function useTooltip({
  defaultIsShown = false,
  placement = "top",
  isShown: controlledIsShown,
  onToggle,
  onHide,
  onShow,
  referenceElement,
  trigger = "auto",
  outsideClickCloseFilter,
  hideWithIntent,
  inputIntent,
  modal = false
}) {
  const parentRef = React.useRef(null);
  const arrowRef = React.useRef(null);
  const [reason, setReason] = React.useState(void 0);
  const [uncontrolledIsShown, setUncontrolledIsShown] = React.useState(defaultIsShown);
  const open = React.useMemo(() => {
    return (onToggle || onHide && onShow) && typeof controlledIsShown !== "undefined" ? controlledIsShown : uncontrolledIsShown;
  }, [onToggle, onHide, onShow, controlledIsShown, uncontrolledIsShown]);
  const handleOpenChange = React.useCallback(
    (nextOpen, _e, changeReason) => {
      const normalizedChangeReason = changeReason === "safe-polygon" ? "hover" : changeReason;
      setReason(normalizedChangeReason);
      if (typeof controlledIsShown === "undefined") {
        setUncontrolledIsShown(nextOpen);
      }
      if (nextOpen !== open) {
        if (onHide && nextOpen === false) {
          onHide();
        }
        if (onShow && nextOpen === true) {
          onShow();
        }
        if (onToggle) {
          onToggle(nextOpen);
        }
      }
    },
    [onToggle, onHide, onShow, controlledIsShown, open]
  );
  const toggle = React.useCallback(() => {
    handleOpenChange(!open);
  }, [handleOpenChange, open]);
  const { headerHeight, isHeaderHidden, headerBottomHeight } = React.useContext(HeaderContext);
  const padding = {
    top: isHeaderHidden ? (
      // employee header is always visible
      headerHeight - headerBottomHeight + 8
    ) : headerHeight + 8,
    right: 8,
    bottom: 8,
    left: 8
  };
  const data = useFloating({
    placement,
    open,
    onOpenChange: handleOpenChange,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(18),
      flip({
        crossAxis: placement.includes("-"),
        fallbackAxisSideDirection: "end",
        padding
      }),
      shift({ padding }),
      size({
        padding,
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: "".concat(availableHeight, "px")
          });
        }
      }),
      arrow({
        element: arrowRef,
        padding: 8
      })
    ]
  });
  const { context, refs } = data;
  React.useEffect(() => {
    let triggerRef = null;
    if (React.isValidElement(referenceElement)) {
      return;
    }
    if (typeof referenceElement === "string") {
      triggerRef = document.querySelector(referenceElement);
    } else if (referenceElement instanceof SVGElement || referenceElement instanceof HTMLElement) {
      triggerRef = referenceElement;
    } else if (refs.floating.current && !parentRef.current) {
      triggerRef = refs.floating.current.parentElement;
      parentRef.current = refs.floating.current.parentElement;
    }
    if (trigger !== "none") {
      if (parentRef.current) {
        parentRef.current.addEventListener("click", toggle);
      } else if (triggerRef) {
        triggerRef.addEventListener("click", toggle);
      }
    }
    if (triggerRef) {
      refs.setReference(triggerRef);
    }
    return () => {
      if (trigger !== "none") {
        if (parentRef.current) {
          parentRef.current.removeEventListener("click", toggle);
        } else if (triggerRef) {
          triggerRef.removeEventListener("click", toggle);
        }
      }
    };
  });
  const hover = useHover(context, {
    handleClose: safePolygon({
      blockPointerEvents: true
    }),
    enabled: trigger === "auto" && !(hideWithIntent == null ? void 0 : hideWithIntent.includes(inputIntent)) && !modal
  });
  const click = useClick(context, {
    enabled: trigger !== "none" && !(hideWithIntent == null ? void 0 : hideWithIntent.includes(inputIntent))
  });
  const dismiss = useDismiss(context, {
    outsidePress: (event) => {
      let target = event.target;
      while (target !== null) {
        if (target instanceof Element) {
          if (outsideClickCloseFilter == null ? void 0 : outsideClickCloseFilter(target)) {
            return false;
          }
          target = target.parentElement;
        }
      }
      return true;
    }
  });
  const role = useRole(context, {
    role: "dialog"
  });
  const interactions = useInteractions([hover, click, dismiss, role]);
  const transitionStatus = useTransitionStatus(context, { duration: 100 });
  return React.useMemo(
    () => ({
      open,
      setOpen: handleOpenChange,
      arrowRef,
      reason,
      ...interactions,
      ...data,
      ...transitionStatus
    }),
    [open, handleOpenChange, interactions, data, transitionStatus, reason]
  );
}
export {
  Tooltip
};
